import React, { useEffect, useRef, useState } from 'react';
import io from 'socket.io-client';
import { iceServers } from '../../../config/iceServers';

interface MirroringStreamProps {
  sessionId: string;
  isInitiator: boolean;
}

const MirroringStream: React.FC<MirroringStreamProps> = ({ sessionId, isInitiator }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [peerConnection, setPeerConnection] = useState<RTCPeerConnection | null>(null);
  const [socket, setSocket] = useState<any>(null);
  const [mirroringActive, setMirroringActive] = useState(false);

  // Utilizziamo un ref per mantenere i candidati ICE in sospeso
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  useEffect(() => {
    // Crea il client socket
    const socketClient = io(process.env.REACT_APP_SIGNALING_URL || 'http://localhost:3002', {
      transports: ['websocket', 'polling'],
      withCredentials: true});
    setSocket(socketClient);

    // Crea la PeerConnection
    const pc = new RTCPeerConnection({ iceServers });
    setPeerConnection(pc);

    // Unisciti alla stanza con il sessionId
    socketClient.emit("join", { sessionId });

    // Gestione dei candidati ICE in uscita
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        socketClient.emit('ice-candidate', { sessionId, candidate: event.candidate });
      }
    };

    // Quando arriva un flusso, lo assegniamo all'elemento video
    pc.ontrack = (event) => {
      console.log("Track ricevuto:", event.track);
      // Se event.streams[0] è undefined, crea un nuovo MediaStream dal track
      if (videoRef.current) {
        // Se event.streams non è disponibile, usiamo event.track per creare uno stream
        const stream = event.streams && event.streams[0]
          ? event.streams[0]
          : new MediaStream([event.track]);
        videoRef.current.srcObject = stream;
      }
    };
    

    // Ricezione dell'offerta
    socketClient.on('offer', async (data: any) => {
      console.log('[Browser] Ricevuta offer:', data);
      if (!isInitiator && data.sessionId === sessionId) {
        try {
          // Imposta la remote description
          await pc.setRemoteDescription(new RTCSessionDescription({ type: data.type, sdp: data.sdp }));

          // Aggiungi i candidati ICE pendenti
          for (const candidateInit of pendingIceCandidatesRef.current) {
            try {
              await pc.addIceCandidate(new RTCIceCandidate(candidateInit));
            } catch (e) {
              console.error('Errore nell\'aggiungere candidato ICE pendente', e);
            }
          }
          pendingIceCandidatesRef.current = [];

          // Crea l'answer e invialo
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socketClient.emit('answer', { type: 'answer', sessionId, sdp: answer.sdp });
        } catch (err) {
          console.error('Errore nel gestire l\'offer:', err);
        }
      } else {
        console.warn('[Browser] Offer non destinata a questo sessionId o client initiator:', data.sessionId, '!==', sessionId);
      }
    });

    // Ricezione della risposta
    socketClient.on('answer', async (data: any) => {
      if (isInitiator && data.sessionId === sessionId) {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription({ type: data.type, sdp: data.sdp }));
        } catch (err) {
          console.error('Errore nel gestire l\'answer:', err);
        }
      }
    });

    // Ricezione dei candidati ICE
    socketClient.on('ice-candidate', async (data: any) => {
      if (data.sessionId === sessionId && data.candidate) {
        // Costruiamo l'oggetto candidato
        const candidateInit: RTCIceCandidateInit = { candidate: data.candidate };
        if (data.sdpMid != null) candidateInit.sdpMid = data.sdpMid;
        if (data.sdpMLineIndex != null) candidateInit.sdpMLineIndex = data.sdpMLineIndex;

        // Se entrambi sono null, logghiamo e ignoriamo il candidato
        if (candidateInit.sdpMid == null && candidateInit.sdpMLineIndex == null) {
          console.warn('Candidate missing sdpMid and sdpMLineIndex, skipping candidate:', candidateInit.candidate);
          return;
        }

        if (!pc.remoteDescription) {
          // Se non abbiamo ancora la remoteDescription, aggiungiamo il candidato alla coda
          pendingIceCandidatesRef.current.push(candidateInit);
        } else {
          try {
            await pc.addIceCandidate(new RTCIceCandidate(candidateInit));
          } catch (e) {
            console.error('Errore nell\'aggiungere candidato ICE', e);
          }
        }
      }
    });

    return () => {
      pc.close();
      socketClient.disconnect();
    };
  }, [sessionId, isInitiator]);

  // Funzione per avviare lo streaming (per il client initiator)
  const startMirroring = async () => {
    if (!isInitiator) {
      console.log("Questo client è un viewer, non cattura media");
      return;
    }
    if (peerConnection) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        console.log("Numero di tracce video:", stream.getVideoTracks().length);
        stream.getTracks().forEach((track) => peerConnection.addTrack(track, stream));
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        socket?.emit('offer', { type: 'offer', sessionId, sdp: offer.sdp });
        setMirroringActive(true);
      } catch (err) {
        console.error('Errore nell\'ottenere i media:', err);
      }
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
      {isInitiator && !mirroringActive && (
        <button
          style={{ position: 'absolute', bottom: '10px', right: '10px', padding: '5px 10px' }}
          onClick={startMirroring}
        >
          Avvia Mirroring
        </button>
      )}
    </div>
  );
};

export default MirroringStream;